import React, { useRef } from 'react';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { BuildingIcon, AlertCircleIcon, UploadIcon, XIcon } from 'lucide-react';
export interface CompanyData {
  id: string;
  name: string;
  email: string;
  phone: string;
  alias: string;
  stripe_public_key: string;
  stripe_private_key: string;
  logo?: string;
  tax_rate?: number;
  invoice_email_template?: string;
  invoice_email_subject_template?: string;
  invoice_sms_template?: string;
  otp_email_template?: string;
  otp_email_subject_template?: string;
}
export interface EditCompanyGeneralProps {
  editingCompany: CompanyData;
  isLoadingCompanyDetails: boolean;
  companyDetailsError: string | null;
  companyName: string;
  setCompanyName: (v: string) => void;
  companyEmail: string;
  setCompanyEmail: (v: string) => void;
  companyPhone: string;
  setCompanyPhone: (v: string) => void;
  companyTaxRate: number;
  setCompanyTaxRate: (v: number) => void;
  companyStripePublicKey: string;
  setCompanyStripePublicKey: (v: string) => void;
  companyStripePrivateKey: string;
  setCompanyStripePrivateKey: (v: string) => void;
  enterNewPrivateKey: boolean;
  setEnterNewPrivateKey: (v: boolean) => void;
  companyLogo: string | null;
  setCompanyLogo: (v: string | null) => void;
  logoChanged: boolean;
  setLogoChanged: (v: boolean) => void;
  companyErrors: Record<string, string>;
  setCompanyErrors: (v: Record<string, string>) => void;
  isSavingCompany: boolean;
  handleSaveCompany: (overrides?: {
    invoiceEmailTemplate?: string;
    otpEmailTemplate?: string;
  }) => void;
  closeEditCompany: () => void;
  openEditCompany: (company: CompanyData) => void;
}
export function EditCompanyGeneral({
  editingCompany,
  isLoadingCompanyDetails,
  companyDetailsError,
  companyName,
  setCompanyName,
  companyEmail,
  setCompanyEmail,
  companyPhone,
  setCompanyPhone,
  companyTaxRate,
  setCompanyTaxRate,
  companyStripePublicKey,
  setCompanyStripePublicKey,
  companyStripePrivateKey,
  setCompanyStripePrivateKey,
  enterNewPrivateKey,
  setEnterNewPrivateKey,
  companyLogo,
  setCompanyLogo,
  logoChanged,
  setLogoChanged,
  companyErrors,
  setCompanyErrors,
  isSavingCompany,
  handleSaveCompany,
  closeEditCompany,
  openEditCompany
}: EditCompanyGeneralProps) {
  const logoInputRef = useRef<HTMLInputElement>(null);
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setCompanyErrors({
        ...companyErrors,
        logo: 'Please select an image file'
      });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setCompanyErrors({
        ...companyErrors,
        logo: 'Image must be less than 2MB'
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const base64Data = base64.split(',')[1];
      setCompanyLogo(base64Data);
      setLogoChanged(true);
      setCompanyErrors({
        ...companyErrors,
        logo: undefined
      } as Record<string, string>);
    };
    reader.onerror = () => {
      setCompanyErrors({
        ...companyErrors,
        logo: 'Failed to read image file'
      });
    };
    reader.readAsDataURL(file);
  };
  const handleRemoveLogo = () => {
    setCompanyLogo(null);
    setLogoChanged(true);
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };
  if (isLoadingCompanyDetails) {
    return <Card data-id="element-2740">
        <div className="p-12 text-center" data-id="element-2741">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2742"></div>
          <p className="mt-4 text-sm text-gray-500" data-id="element-2743">
            Loading company details...
          </p>
        </div>
      </Card>;
  }
  if (companyDetailsError) {
    return <Card data-id="element-2744">
        <div className="p-12 text-center" data-id="element-2745">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-2746">
            <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-2747" />
          </div>
          <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2748">
            Failed to load company details
          </p>
          <p className="text-sm text-gray-500 mb-4" data-id="element-2749">{companyDetailsError}</p>
          <div className="flex justify-center gap-3" data-id="element-2750">
            <Button variant="secondary" size="sm" onClick={closeEditCompany} data-id="element-2751">
              Back to Settings
            </Button>
            <Button size="sm" onClick={() => openEditCompany(editingCompany)} data-id="element-2752">
              Retry
            </Button>
          </div>
        </div>
      </Card>;
  }
  return <Card data-id="element-2753">
      <div className="space-y-6" data-id="element-2754">
        {/* Logo Upload */}
        <div data-id="element-2755">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2756">
            Company Logo
          </label>
          <div className="flex items-center gap-4" data-id="element-2757">
            {companyLogo ? <div className="relative" data-id="element-2758">
                <img src={`data:image/png;base64,${companyLogo}`} alt="Company logo" className="w-20 h-20 rounded-lg object-cover border border-gray-200" data-id="element-2759" />
                <button type="button" onClick={handleRemoveLogo} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors" aria-label="Remove logo" data-id="element-2760">
                  <XIcon className="w-4 h-4" data-id="element-2761" />
                </button>
              </div> : <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200" data-id="element-2762">
                <BuildingIcon className="w-10 h-10 text-gray-400" data-id="element-2763" />
              </div>}
            <div data-id="element-2764">
              <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" data-id="element-2765" />
              <Button variant="secondary" size="sm" onClick={() => logoInputRef.current?.click()} leftIcon={<UploadIcon className="w-4 h-4" data-id="element-2767" />} data-id="element-2766">
                {companyLogo ? 'Change Logo' : 'Upload Logo'}
              </Button>
              <p className="text-xs text-gray-500 mt-1" data-id="element-2768">PNG, JPG up to 2MB</p>
            </div>
          </div>
          {companyErrors.logo && <p className="text-sm text-red-600 mt-1" data-id="element-2769">{companyErrors.logo}</p>}
        </div>

        {/* Form Fields in Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2770">
          <Input label="Company Name" value={companyName} onChange={e => setCompanyName(e.target.value)} error={companyErrors.name} placeholder="e.g., Acme Corporation" data-id="element-2771" />
          <Input label="Email" type="email" value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} error={companyErrors.email} placeholder="billing@company.com" data-id="element-2772" />
          <Input label="Phone" type="tel" value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} placeholder="(555) 123-4567" data-id="element-2773" />
          <Input label="Tax Rate (%)" type="number" min="0" max="100" step="1" value={companyTaxRate} onChange={e => setCompanyTaxRate(parseFloat(e.target.value) || 0)} error={companyErrors.tax_rate} placeholder="e.g., 6" helperText="Enter tax rate as a percentage (0-100)" data-id="element-2774" />
        </div>

        {/* Stripe Settings Section */}
        <div className="border-t border-gray-200 pt-6" data-id="element-2775">
          <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-2776">
            Stripe Integration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2777">
            <Input label="Stripe Public Key" value={companyStripePublicKey} onChange={e => setCompanyStripePublicKey(e.target.value)} error={companyErrors.stripe_public_key} placeholder="pk_live_..." data-id="element-2778" />
            {!editingCompany?.stripe_public_key ? <Input label="Stripe Private Key" type="password" value={companyStripePrivateKey} onChange={e => setCompanyStripePrivateKey(e.target.value)} error={companyErrors.stripe_private_key} placeholder="sk_live_..." helperText="Your private key is encrypted and stored securely" data-id="element-2779" /> : <div className="space-y-3" data-id="element-2780">
                <Checkbox label="Enter new Stripe private key" checked={enterNewPrivateKey} onChange={e => {
              setEnterNewPrivateKey(e.target.checked);
              if (!e.target.checked) setCompanyStripePrivateKey('');
            }} data-id="element-2781" />
                {enterNewPrivateKey && <Input label="Stripe Private Key" type="password" value={companyStripePrivateKey} onChange={e => setCompanyStripePrivateKey(e.target.value)} error={companyErrors.stripe_private_key} placeholder="sk_live_..." helperText="Your private key is encrypted and stored securely" data-id="element-2782" />}
              </div>}
          </div>
        </div>

        {companyErrors.submit && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-2783">
            <p className="text-sm text-red-600" data-id="element-2784">{companyErrors.submit}</p>
          </div>}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-2785">
          <Button variant="secondary" onClick={closeEditCompany} disabled={isSavingCompany} data-id="element-2786">
            Cancel
          </Button>
          <Button onClick={handleSaveCompany} isLoading={isSavingCompany} data-id="element-2787">
            Save Changes
          </Button>
        </div>
      </div>
    </Card>;
}